import Localforage from "localforage"
import {ForageKeys} from "../types/gen-types";
import {useLayoutEffect, useState } from "react";
import cfg from "../config/general-config";
import { useRouteMatch } from "react-router-dom";

const api = cfg.api
export const apiUrls = {
    gql: () => {
        if (api.portless) {
            return `${cfg.protocol}://${api.host}/${api.gqlPath}`
        } else {
            return `${cfg.protocol}://${api.host}:${api.port}/${api.gqlPath}`
        }
    },
    rest: () => {
        if (api.portless) {
            return `${cfg.protocol}://${api.host}`
        } else {
            return `${cfg.protocol}://${api.host}:${api.port}`
        }
    }
}

/**
 * Great helper function to allow type-checking of object keys in places
 * where using direct type is NOT allowed (e.g. Mongo `sort`)
 */
export const nameof = <T>(key: keyof T): keyof T => key

/**
 *
 * @param state
 */
export const prepForPush = (state: any) => {
    let tmp = Object.assign({}, state)
    return stripTypename(tmp)
}

/**
 * All Apollo Client query responses include the `__typename` field, but when returning data
 * to a mutation to update a state in the database, we CANNOT include the `__typename` because the
 * Graph schema is NOT defined to include it, so we have to strip it
 *
 * @sources
 *  - https://stackoverflow.com/questions/47211778/cleaning-unwanted-fields-from-graphql-responses/51380645#51380645
 * @param state
 */
export const stripTypename = (state: any) => {
    let tmp = JSON.stringify(
        state,
        (key, value) => (key === '__typename' ? undefined : value)
    )
    return JSON.parse(tmp)
}

/**
 * React Hook: Extract active router path
 */
export function useActivePath() {
    const match = useRouteMatch()
    return match.url
}

/**
 * Helpful tool to convert hex color code to rgb w/ custom opacity
 *
 * @sources
 *  - https://gist.github.com/danieliser/b4b24c9f772066bcf0a6
 *
 * @param hexCode
 * @param opacity - 0-100
 */
export const hex2rgba = (hexCode: string, opacity: number) => {
    let hex = hexCode.replace('#', '');

    if (hex.length === 3) {
        hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `rgba(${r},${g},${b},${opacity / 100})`;
}


/**
 * Shortcut for localforage to avoid having to import it in each script
 */
export const forage = Localforage
export const forageSet = <T>(key: ForageKeys, value: T) => Localforage.setItem<T>(key, value)
export const forageGet = <T>(key: ForageKeys) => Localforage.getItem<T>(key)

/**
 * Custom hook to detect change of window size in order to effect custom operations (e.g. re-render)
 * when window size changes (browser window resize)
 *
 * https://stackoverflow.com/questions/19014250/rerender-view-on-browser-resize-with-react
 */
export function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}